import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { assign } from 'lodash'
import { Card, Button, Switch, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core'
import FormItem from 'components/FormItem'
import { isBlank } from 'common/usual'
import {
  partnerDetailPromise,
  partnerCreatePromise,
  partnerUpdatePromise,
  partnerRotateSecretPromise,
} from 'api/Partner'

export default function PartnerUpsert (props) {
  const id = props.match.params.id
  const [partner, setPartner] = useState({ active: true })
  const [btnStatus, setBtnStatus] = useState(false)
  // Held in component state only. It is in the response once and is never readable again, so
  // leaving the screen without copying it means rotating a second time.
  const [secret, setSecret] = useState(null)
  const [confirmRotate, setConfirmRotate] = useState(false)

  const disableBtn = useMemo(() => btnStatus || isBlank(partner.name), [btnStatus, partner.name])

  const getDetail = useCallback(async () => {
    if (!id) return
    setPartner(await partnerDetailPromise(id))
  }, [id])

  useEffect(() => { getDetail() }, [getDetail])

  const handleChange = useCallback((...values) => {
    setPartner(current => assign({}, current, ...values))
  }, [])

  const commonProps = useMemo(() => ({
    onChange: handleChange,
    data: partner
  }), [handleChange, partner])

  function handleSubmit () {
    setBtnStatus(true)

    const body = {
      name: partner.name,
      partnerExternalId: partner.partnerExternalId,
      logo: partner.logo,
      active: !!partner.active,
      appsflyerMediaSource: partner.appsflyerMediaSource,
      appsflyerCampaign: partner.appsflyerCampaign,
      enablePartnerWorkoutResource: !!partner.enablePartnerWorkoutResource,
      enablePartnerWorkoutCategory: !!partner.enablePartnerWorkoutCategory,
      enablePartnerChangeableWorkout: !!partner.enablePartnerChangeableWorkout,
      enablePartnerChallenge: !!partner.enablePartnerChallenge,
      enableAutoPost: !!partner.enableAutoPost,
      amplitudeApiKey: partner.amplitudeApiKey,
      promoId: partner.promoId ? Number(partner.promoId) : undefined,
      partnerLogSession: !!partner.partnerLogSession,
      // Blank, not falsy: 0 is a real TTL, and clearing the field has to send null to unset the
      // stored one rather than omitting the key and keeping it.
      jwtTokenTtlSeconds: isBlank(partner.jwtTokenTtlSeconds) ? null : Number(partner.jwtTokenTtlSeconds),
    }

    // `updatedAt` goes on update only -- it is the optimistic-concurrency token the API requires
    // back, and the create schema rejects it as an unknown key.
    const request = id
      ? partnerUpdatePromise(id, { ...body, updatedAt: partner.updatedAt })
      : partnerCreatePromise(body)

    request
      .then(resp => {
        // Create answers with the one and only copy of the secret, so stay on the page and show it
        // rather than navigating back.
        if (!id && resp && resp.clientSecret) return setSecret(resp.clientSecret)
        return props.history.goBack()
      })
      // 409 = the partner changed under us. Refetch so the form shows the current values and
      // carries a fresh token; resubmitting the old one would fail identically forever.
      .catch(err => { if (err && err.status === 409) return getDetail() })
      .finally(() => setBtnStatus(false))
  }

  function handleRotate () {
    setConfirmRotate(false)
    setBtnStatus(true)

    partnerRotateSecretPromise(id, { updatedAt: partner.updatedAt })
      .then(resp => {
        if (resp && resp.clientSecret) setSecret(resp.clientSecret)
        return getDetail()
      })
      .catch(err => { if (err && err.status === 409) return getDetail() })
      .finally(() => setBtnStatus(false))
  }

  return (
    <div className={'root'}>
      <Card className={'form-container'}>
        <FormItem label={'Name'} name={'name'} isRequired {...commonProps} />
        <FormItem label={'External ID'} name={'partnerExternalId'} {...commonProps} />
        <FormItem label={'Logo'} name={'logo'} {...commonProps} />
        <FormItem label={'Appsflyer Media Source'} name={'appsflyerMediaSource'} {...commonProps} />
        <FormItem label={'Appsflyer Campaign'} name={'appsflyerCampaign'} {...commonProps} />
        <FormItem label={'Amplitude API Key'} name={'amplitudeApiKey'} {...commonProps} />
        <FormItem label={'Promo ID'} name={'promoId'} type={'number'} {...commonProps} />
        <FormItem
          label={'Token TTL (seconds)'}
          name={'jwtTokenTtlSeconds'}
          type={'number'}
          title={'Partner logins are the only tokens that expire. Empty means no expiry.'}
          {...commonProps} />

        { id && <FormItem label={'Client ID'} name={'clientId'} readonly {...commonProps} /> }

        <FormItem label={'Active'} name={'active'}>
          <Switch checked={Boolean(partner.active)} onChange={event => handleChange({ active: event.target.checked })} name='active' />
        </FormItem>
        <FormItem label={'Partner Workout Resource'} name={'enablePartnerWorkoutResource'}>
          <Switch checked={Boolean(partner.enablePartnerWorkoutResource)} onChange={event => handleChange({ enablePartnerWorkoutResource: event.target.checked })} />
        </FormItem>
        <FormItem label={'Partner Workout Category'} name={'enablePartnerWorkoutCategory'}>
          <Switch checked={Boolean(partner.enablePartnerWorkoutCategory)} onChange={event => handleChange({ enablePartnerWorkoutCategory: event.target.checked })} />
        </FormItem>
        <FormItem label={'Changeable Workout'} name={'enablePartnerChangeableWorkout'}>
          <Switch checked={Boolean(partner.enablePartnerChangeableWorkout)} onChange={event => handleChange({ enablePartnerChangeableWorkout: event.target.checked })} />
        </FormItem>
        <FormItem label={'Partner Challenge'} name={'enablePartnerChallenge'}>
          <Switch checked={Boolean(partner.enablePartnerChallenge)} onChange={event => handleChange({ enablePartnerChallenge: event.target.checked })} />
        </FormItem>
        <FormItem label={'Auto Post'} name={'enableAutoPost'}>
          <Switch checked={Boolean(partner.enableAutoPost)} onChange={event => handleChange({ enableAutoPost: event.target.checked })} />
        </FormItem>
        <FormItem label={'Log Session'} name={'partnerLogSession'}>
          <Switch checked={Boolean(partner.partnerLogSession)} onChange={event => handleChange({ partnerLogSession: event.target.checked })} />
        </FormItem>

        <p className='tips'>
          Credentials are generated by the API -- they cannot be typed in here. The secret is shown
          once, on create and on rotation, and never again. Saving drops the partner cache, so a
          change takes effect on the next request.
        </p>

        <Button
          className='save-btn'
          color='primary'
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          onClick={handleSubmit}
          disabled={disableBtn}>
          Save
        </Button>

        { id &&
          <Button
            className='save-btn'
            fullWidth
            size='large'
            variant='outlined'
            disabled={btnStatus}
            onClick={() => setConfirmRotate(true)}>
            Rotate client secret
          </Button>
        }
      </Card>

      <Dialog open={confirmRotate} onClose={() => setConfirmRotate(false)}>
        <DialogTitle>Rotate the client secret?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current secret stops working as soon as this lands. There is no grace period and no
            second active secret, so {partner.name} is locked out until they redeploy with the new
            one. Have someone ready to receive it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRotate(false)}>Cancel</Button>
          <Button color='primary' onClick={handleRotate}>Rotate</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!secret} onClose={() => { setSecret(null); props.history.push('/partner-accounts') }}>
        <DialogTitle>Copy the client secret now</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is the only time it is readable. It is stored for authentication but never returned
            again -- losing it means rotating, which locks the partner out until they redeploy.
          </DialogContentText>
          <FormTextSecret value={secret} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigator.clipboard && navigator.clipboard.writeText(secret)}>Copy</Button>
          <Button color='primary' onClick={() => { setSecret(null); props.history.push('/partner-accounts') }}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

// Selectable and wrapping: the secret is 64 characters, and an admin copying it by eye is the
// failure this dialog exists to prevent.
function FormTextSecret ({ value }) {
  return (
    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all', padding: '8px 0' }}>
      {value}
    </pre>
  )
}
